/**
 * Material cost breakdowns per medium — used by PhaseAccumulator
 * to let users build up material costs from common supply items.
 * Costs are in whole dollars.
 */

import type { MediumKey } from '@/types';

/**
 * Size-scaled material estimate constants per medium.
 * Formula: materialEstimate = baseFixed + (perSqIn × area)
 * Source: P7 remediation — replaces flat per-medium presets.
 * Units: dollars (baseFixed) and dollars/sq in (perSqIn).
 */
export interface MaterialEstimateConfig {
  baseFixed: number;  // dollars — fixed cost (brushes, supplies, etc.)
  perSqIn: number;    // dollars per sq in — scales with canvas size
}

export const MATERIAL_ESTIMATES: Record<MediumKey, MaterialEstimateConfig> = {
  oil:         { baseFixed: 25, perSqIn: 0.12 },
  acrylic:     { baseFixed: 18, perSqIn: 0.08 },
  watercolor:  { baseFixed: 15, perSqIn: 0.04 },
  gouache:     { baseFixed: 15, perSqIn: 0.04 },
  pastel:      { baseFixed: 20, perSqIn: 0.06 },
  mixed_media: { baseFixed: 20, perSqIn: 0.10 },
};

/**
 * Compute size-scaled material estimate in cents.
 * Returns 0 if area is not positive.
 */
export function estimateMaterialCents(medium: MediumKey, area: number): number {
  if (area <= 0) return 0;
  const config = MATERIAL_ESTIMATES[medium];
  const dollars = config.baseFixed + config.perSqIn * area;
  return Math.round(dollars * 100);
}

/**
 * Framing cost brackets for paper-based mediums (watercolor, pastel, gouache).
 * Stepped model — framing has discrete cost jumps at standard frame sizes.
 * Source: P8 remediation.
 * Units: dollars.
 */
export interface FramingBracket {
  maxArea: number;   // sq in upper bound (inclusive)
  estimate: number;  // dollars
}

export const FRAMING_BRACKETS: FramingBracket[] = [
  { maxArea: 100,            estimate: 55 },   // small standard frame + mat
  { maxArea: 400,            estimate: 95 },   // medium custom frame + mat + glass
  { maxArea: 800,            estimate: 145 },  // large custom frame
  { maxArea: Infinity,       estimate: 200 },  // oversized/specialty
];

/**
 * Get framing cost estimate in dollars for a given area.
 * Only meaningful for paper-based mediums.
 */
export function getFramingEstimate(area: number): number {
  if (area <= 0) return 0;
  const bracket = FRAMING_BRACKETS.find(b => area <= b.maxArea);
  return bracket?.estimate ?? 200;
}

/**
 * Get framing cost estimate in cents for a given area.
 */
export function getFramingEstimateCents(area: number): number {
  return getFramingEstimate(area) * 100;
}

export interface MaterialItem {
  label: string;
  cost: number; // dollars
}

/**
 * Size-scaled wizard material items.
 * Each item: base (fixed $) + rate ($/sq in).
 * Calibrated against mid-range artist-grade retail (Golden, Gamblin, W&N).
 * Canvas reference: 1.5" gallery profile, single unit, mid-range (Blick Premier / Fredrix Red Label).
 *   8×10 (~$15), 16×20 (~$28-35), 24×36 (~$58), 30×40 (~$76), 48×60 (~$130)
 * Source: Michaels/Blick/Jerry's retail, Apr 2026.
 */
interface ScaledItem {
  label: string;
  base: number;   // fixed cost ($)
  rate: number;   // $/sq in
}

const SCALED_WIZARD_ITEMS: Record<MediumKey, ScaledItem[]> = {
  oil: [
    { label: 'Canvas',    base: 10, rate: 0.055 },
    { label: 'Oil paint', base: 12, rate: 0.040 },
    { label: 'Medium',    base: 5,  rate: 0.005 },
    { label: 'Varnish',   base: 4,  rate: 0.006 },
    { label: 'Brushes',   base: 8,  rate: 0 },
  ],
  acrylic: [
    { label: 'Canvas',        base: 10, rate: 0.055 },
    { label: 'Acrylic paint', base: 8,  rate: 0.028 },
    { label: 'Gel medium',    base: 4,  rate: 0.005 },
    { label: 'Varnish',       base: 3,  rate: 0.005 },
    { label: 'Brushes',       base: 8,  rate: 0 },
  ],
  watercolor: [
    { label: 'Watercolor paper',  base: 3, rate: 0.018 },
    { label: 'Watercolor paint',  base: 12, rate: 0.012 },
    { label: 'Masking fluid',     base: 5, rate: 0 },
    { label: 'Brushes',           base: 8, rate: 0 },
  ],
  gouache: [
    { label: 'Paper or board',  base: 4,  rate: 0.016 },
    { label: 'Gouache paint',   base: 10, rate: 0.014 },
    { label: 'Fixative',        base: 6,  rate: 0 },
    { label: 'Brushes',         base: 8,  rate: 0 },
  ],
  pastel: [
    { label: 'Pastel paper',  base: 4,  rate: 0.018 },
    { label: 'Soft pastels',  base: 18, rate: 0.010 },
    { label: 'Hard pastels',  base: 10, rate: 0.005 },
    { label: 'Fixative',      base: 7,  rate: 0 },
  ],
  mixed_media: [
    { label: 'Substrate',            base: 5,  rate: 0.035 },
    { label: 'Primary medium',       base: 10, rate: 0.025 },
    { label: 'Secondary materials',  base: 6,  rate: 0.012 },
    { label: 'Adhesives',            base: 7,  rate: 0 },
    { label: 'Sealant',              base: 5,  rate: 0.003 },
  ],
};

/**
 * Returns wizard material items with costs scaled to the given area.
 * Falls back to a 100 sq in baseline if area is invalid.
 */
export function getScaledWizardMaterials(medium: MediumKey, area: number): MaterialItem[] {
  const a = area > 0 ? area : 100;
  const items = SCALED_WIZARD_ITEMS[medium] || SCALED_WIZARD_ITEMS.oil;
  return items.map(({ label, base, rate }) => ({
    label,
    cost: Math.round(base + rate * a),
  }));
}

/** @deprecated — use getScaledWizardMaterials(medium, area) instead */
export const WIZARD_MATERIALS: Record<MediumKey, MaterialItem[]> = {
  oil: [
    { label: 'Canvas', cost: 16 },
    { label: 'Oil paint', cost: 28 },
    { label: 'Medium', cost: 10 },
    { label: 'Varnish', cost: 9 },
    { label: 'Brushes', cost: 8 },
  ],
  acrylic: [
    { label: 'Canvas', cost: 16 },
    { label: 'Acrylic paint', cost: 20 },
    { label: 'Gel medium', cost: 7 },
    { label: 'Varnish', cost: 7 },
    { label: 'Brushes', cost: 8 },
  ],
  watercolor: [
    { label: 'Watercolor paper', cost: 8 },
    { label: 'Watercolor paint', cost: 18 },
    { label: 'Masking fluid', cost: 6 },
    { label: 'Brushes', cost: 8 },
  ],
  gouache: [
    { label: 'Paper or board', cost: 10 },
    { label: 'Gouache paint', cost: 16 },
    { label: 'Fixative', cost: 7 },
    { label: 'Brushes', cost: 8 },
  ],
  pastel: [
    { label: 'Pastel paper', cost: 10 },
    { label: 'Soft pastels', cost: 24 },
    { label: 'Hard pastels', cost: 14 },
    { label: 'Fixative', cost: 8 },
  ],
  mixed_media: [
    { label: 'Substrate', cost: 14 },
    { label: 'Primary medium', cost: 22 },
    { label: 'Secondary materials', cost: 12 },
    { label: 'Adhesives', cost: 8 },
    { label: 'Sealant', cost: 7 },
  ],
};
